import React, { useState, useEffect, useRef } from 'react';
import { EdgeProps, useReactFlow } from '@xyflow/react';

export const CustomTwoPointEdge = (props: EdgeProps) => {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    data,
    selected,
    style = {},
  } = props;

  const stroke = style.stroke || '#000';
  const strokeWidth = Number(style.strokeWidth) || 1;
  const interactionWidth = 20;

  const arrowColor = stroke;
  const arrowWidth = 6;
  const arrowLength = 10;
  const highlightColor = stroke;
  const glowColor = stroke;

  // Cập nhật lại control1 và control2 cho ba đoạn thẳng
  const [control1, setControl1] = useState<{ x: number; y: number }>({
    x: Number(data?.control1X) || (sourceX + targetX) / 3,
    y: Number(data?.control1Y) || (sourceY + targetY) / 3,
  });
  const [control2, setControl2] = useState<{ x: number; y: number }>({
    x: Number(data?.control2X) || (sourceX + targetX) * 2 / 3,
    y: Number(data?.control2Y) || (sourceY + targetY) * 2 / 3,
  });

  const prevEndpoints = useRef({ sourceX, sourceY, targetX, targetY });

  useEffect(() => {
    if (data?.control1X != null && data?.control1Y != null && data?.control2X != null && data?.control2Y != null) return;

    const newControl1 = {
      x: (sourceX + targetX) / 3,
      y: (sourceY + targetY) / 3,
    };
    const newControl2 = {
      x: (sourceX + targetX) * 2 / 3,
      y: (sourceY + targetY) * 2 / 3,
    };

    setControl1(newControl1);
    setControl2(newControl2);
    prevEndpoints.current = { sourceX, sourceY, targetX, targetY };
  }, [sourceX, sourceY, targetX, targetY]);

  const { updateEdgeData } = useReactFlow();
  useEffect(() => {
    updateEdgeData(id, { control1X: control1.x, control1Y: control1.y, control2X: control2.x, control2Y: control2.y });
  }, [control1, control2]);

  // Tạo 3 đường thẳng nối với nhau
  const edgePath = `M ${sourceX},${sourceY} L ${control1.x},${control1.y} L ${control2.x},${control2.y} L ${targetX},${targetY}`;

  const handleDrag = (event: PointerEvent, control: string) => {
    const svg = (event.target as SVGElement).ownerSVGElement;
    if (svg && svg.getScreenCTM) {
      const point = svg.createSVGPoint();
      point.x = event.clientX;
      point.y = event.clientY;
      const transformed = point.matrixTransform(svg.getScreenCTM()?.inverse());
      if (control === 'control1') {
        setControl1({ x: transformed.x, y: transformed.y });
      } else if (control === 'control2') {
        setControl2({ x: transformed.x, y: transformed.y });
      }
    }
  };

  return (
    <>
      <defs>
        <filter id={`glow-${id}`}>
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feFlood floodColor={glowColor} floodOpacity="0.4" />
          <feComposite in2="coloredBlur" operator="in" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <marker
          id={`arrow-${id}`}
          viewBox="0 0 10 10"
          refX={10}
          refY={5}
          markerWidth={arrowWidth}
          markerHeight={arrowLength}
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={arrowColor} />
        </marker>
      </defs>

      {interactionWidth && (
        <path
          d={edgePath}
          fill="none"
          strokeOpacity={0}
          strokeWidth={interactionWidth}
          className="react-flow__edge-interaction"
        />
      )}

      <path id={id} d={edgePath} fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeDasharray={style.strokeDasharray} />
      <path
        id={`${id}B`}
        d={edgePath}
        fill="none"
        stroke={stroke}
        strokeWidth={Math.min(strokeWidth, 2)}
        markerEnd={`url(#arrow-${id})`}
        strokeDasharray={style.strokeDasharray}
        filter={selected ? `url(#glow-${id})` : undefined}
      />
      <circle
        cx={control1.x}
        cy={control1.y}
        r={6}
        fill="#fff"
        stroke={highlightColor}
        strokeWidth={2}
        cursor="move"
        pointerEvents="all"
        onPointerDown={(event) => {
          event.preventDefault();
          const target = event.target as SVGCircleElement;
          target.setPointerCapture(event.pointerId);
          target.onpointermove = (e) => handleDrag(e, 'control1');
          target.onpointerup = () => (target.onpointermove = null);
        }}
      />
      <circle
        cx={control2.x}
        cy={control2.y}
        r={6}
        fill="#fff"
        stroke={highlightColor}
        strokeWidth={2}
        cursor="move"
        pointerEvents="all"
        onPointerDown={(event) => {
          event.preventDefault();
          const target = event.target as SVGCircleElement;
          target.setPointerCapture(event.pointerId);
          target.onpointermove = (e) => handleDrag(e, 'control2');
          target.onpointerup = () => (target.onpointermove = null);
        }}
      />
    </>
  );
};
